import { zodResolver } from "@hookform/resolvers/zod"
import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { z } from "zod"

import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Separator } from "@/components/ui/separator"
import { Textarea } from "@/components/ui/textarea"
import { useCancelSubscription } from "@/hooks"
import { toast } from "@/hooks/use-toast"
import { ErrorHelper } from "@/utils"
const cancelDialogSchema = z.object({
  op1: z.boolean().refine((value) => value, {
    message: "Você precisa concordar com esta opção",
  }),
  op2: z.boolean().refine((value) => value, {
    message: "Você precisa concordar com esta opção",
  }),
  op3: z.boolean().refine((value) => value, {
    message: "Você precisa concordar com esta opção",
  }),
  reason: z
    .string()
    .min(1, { message: "O motivo é obrigatório" }),
})
type CancelDialogSchemaProps = z.infer<
  typeof cancelDialogSchema
>
interface CancelDialogProps {
  id: string
  open: boolean
  setOpen: (open: boolean) => void
}
export const CancelSubscriptionDialog = ({
  id,
  open = false,
  setOpen,
}: CancelDialogProps) => {
  const { t } = useTranslation()
  const defaultValues: CancelDialogSchemaProps = {
    op1: false,
    op2: false,
    op3: false,
    reason: "",
  }
  const form = useForm<CancelDialogSchemaProps>({
    resolver: zodResolver(cancelDialogSchema),
    defaultValues,
  })
  const { mutateAsync: cancelSubscription } =
    useCancelSubscription(id)
  const handleSubmit = form.handleSubmit(async (data) => {
    await cancelSubscription(
      { reason: data.reason, id },
      {
        onSuccess: () => {
          ErrorHelper.showSuccess(
            "Assinatura cancelada com sucesso"
          )
          setOpen(false)
          form.reset(defaultValues)
        },
        onError: () => {
          toast({
            title: t("subscriptions.cancelError"),
            variant: "destructive",
          })
        },
      }
    )
  })
  const formIsPending = form.formState.isSubmitting
  return (
    <AlertDialog onOpenChange={setOpen} open={open}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="text-center text-xl">
            Cancelar assinatura
          </AlertDialogTitle>
          <Separator />
          <AlertDialogDescription>
            Atenção! Esta ação interfere diretamente no
            fluxo da assinatura e não poderá ser desfeita.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div>
          <Form {...form}>
            <form
              onSubmit={handleSubmit}
              className="space-y-4"
            >
              <FormField
                control={form.control}
                name="op1"
                render={({ field }) => {
                  return (
                    <FormItem>
                      <div className="flex flex-row items-start space-x-3 space-y-0">
                        <FormControl>
                          <Checkbox
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                        <div className="space-y-1 leading-none">
                          <FormLabel>
                            Eu entendo que vou interferir no
                            andamento da assinatura.
                          </FormLabel>
                        </div>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )
                }}
              />
              <FormField
                control={form.control}
                name="op2"
                render={({ field }) => {
                  return (
                    <FormItem>
                      <div className="flex flex-row items-start space-x-3 space-y-0">
                        <FormControl>
                          <Checkbox
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                        <div className="space-y-1 leading-none">
                          <FormLabel>
                            Eu entendo que esta ação não
                            poderá ser desfeita.
                          </FormLabel>
                        </div>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )
                }}
              />
              <FormField
                control={form.control}
                name="op3"
                render={({ field }) => {
                  return (
                    <FormItem>
                      <div className="flex flex-row items-start space-x-3 space-y-0">
                        <FormControl>
                          <Checkbox
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                        <div className="space-y-1 leading-none">
                          <FormLabel>
                            Eu entendo as consequências
                            desta ação no fluxo da
                            assinatura.
                          </FormLabel>
                        </div>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )
                }}
              />
              <FormField
                control={form.control}
                name="reason"
                render={({ field }) => {
                  return (
                    <FormItem>
                      <FormControl>
                        <Textarea
                          placeholder="Motivo do cancelamento"
                          className="resize-none text-sm"
                          rows={5}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )
                }}
              />
              <Separator />
              <AlertDialogFooter>
                <AlertDialogCancel disabled={formIsPending}>
                  Cancelar
                </AlertDialogCancel>
                <Button
                  type="submit"
                  variant="destructive"
                  disabled={formIsPending}
                >
                  Confirmar
                </Button>
              </AlertDialogFooter>
            </form>
          </Form>
        </div>
      </AlertDialogContent>
    </AlertDialog>
  )
}
